/* eslint-disable formatjs/enforce-placeholders */
import { useMutation, useQuery } from "@apollo/client";
import classNames from "components/ui/classNames";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  InlineDialogClose,
} from "components/ui/Dialog";
import { Spinner } from "components/ui/Spinner";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { FundamentalProductOriginCodes } from "lib/analytics/FundamenteiProductCodes";
import { getReferralCode } from "lib/referral/referralStorage";
import type {
  IPremiumPricesQuery,
  IPremiumPricesQueryVariables,
} from "lib/graphql/queries/__generated__/PremiumPricesQuery.generated";
import PremiumPricesQuery from "lib/graphql/queries/PremiumPricesQuery";
import type {
  ICheckoutUrlMutation,
  ICheckoutUrlMutationVariables,
} from "lib/queries/__generated__/CheckoutUrlMutation.generated";
import CheckoutUrlMutation from "lib/queries/CheckoutUrlMutation";
import { CheckIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useAsyncCallback } from "react-async-hook";
import toast from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import { ICheckoutProduct, IStripeRecurringPriceInterval } from "types/graphqlTypes";

interface ScreenerExpressCheckoutProps {
  children: ReactNode;
}
export default function ScreenerExpressCheckout({ children }: ScreenerExpressCheckoutProps) {
  const intl = useIntl();

  const amplitude = useAmplitude();

  const { data, loading } = useQuery<IPremiumPricesQuery, IPremiumPricesQueryVariables>(PremiumPricesQuery, {
    fetchPolicy: "network-only",
    variables: {},
  });

  const [checkoutUrl] = useMutation<ICheckoutUrlMutation, ICheckoutUrlMutationVariables>(CheckoutUrlMutation, {});
  const checkoutProduct = useAsyncCallback(async (product: ICheckoutProduct) => {
    try {
      toast.loading(intl.formatMessage({ defaultMessage: "Redirecionando...", id: "E599KU" }));

      amplitude.logEvent("Click on checkout button", {
        origin: FundamentalProductOriginCodes.SCREENER,
      });

      const { data } = await checkoutUrl({
        variables: {
          product,
          coupon: null,
          parameters: {
            bypassFreeTrial: true,
            cancelResourcePath: "/thank-you",
            successResourcePath: "/thank-you",
            locale: intl.locale,
            referralCode: getReferralCode(),
          },
        },
      });

      if (data && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      if (err && err instanceof Error && /You already have Premium access/i.test(err.message)) {
        toast.error(intl.formatMessage({ defaultMessage: "Você já possui acesso ao Premium", id: "nbsstU" }));
        return;
      }

      toast.error(
        intl.formatMessage({ defaultMessage: "Algo errado aconteceu...tente novamente mais tarde", id: "hDtVwS" }),
      );
    }
  });

  const monthlyPrice = data?.premiumPrices.find(
    (node) => node.recurringInterval === IStripeRecurringPriceInterval.Month,
  );

  return (
    <Dialog modal={true}>
      <DialogTrigger asChild={true}>{children}</DialogTrigger>
      <DialogContent
        hideCloseButton={true}
        className="mt-12 max-w-sm rounded-3xl bg-white dark:border-slate-900 dark:bg-slate-700 sm:mt-0 sm:rounded-3xl md:max-w-lg"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-2xl font-black leading-6 dark:text-white lg:leading-7">
            <FormattedMessage defaultMessage="Assine o Premium e libere todos os ativos e indicadores." id="hblZeW" />
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-slate-600 dark:text-slate-300">
            <FormattedMessage defaultMessage="Compare ativos como nunca antes e eleve seus estudos!" id="GEIMQA" />
          </DialogDescription>
        </DialogHeader>

        <InlineDialogClose className="absolute right-4 top-0" />

        <ul className="space-y-2">
          {[
            intl.formatMessage({
              defaultMessage: "<b>6,000+ ativos disponíveis</b>: Ações, FIIs, Stocks e REITs",
              id: "xNW1Xe",
            }),
            intl.formatMessage({
              defaultMessage: "<b>900+ indicadores</b>. Fundamentalistas, preço, governança, etc",
              id: "BLwQkx",
            }),
            intl.formatMessage({
              defaultMessage: "Filtre pela sua carteira. Salve seus screeners",
              id: "b2HEXa",
            }),
            intl.formatMessage({
              defaultMessage: "Aplique filtros customizados. Por exemplo: <b>P/L > 10</b>",
              id: "EmJb/Y",
            }),
          ].map((line) => {
            return (
              <li key={line} className="flex items-center space-x-2 text-balance">
                <div className="rounded-full bg-sky-100 p-1 text-sky-600 dark:bg-teal-400/20 dark:text-teal-200">
                  <CheckIcon className="h-3 w-3" strokeWidth={3} />
                </div>
                <span className="text-sm font-medium tracking-wide text-slate-700 dark:text-slate-100 sm:text-sm [&>strong]:dark:text-teal-200">
                  {line}
                </span>
              </li>
            );
          })}
        </ul>

        {loading && (
          <div className="text-center">
            <Spinner />
          </div>
        )}

        {!monthlyPrice && (
          <p
            className={classNames(
              "rounded-2xl bg-slate-500/5 p-4 text-center text-sm font-medium text-slate-600 dark:bg-rose-800/30 dark:font-semibold dark:text-rose-200",
            )}
          >
            <FormattedMessage
              defaultMessage="Não foi possível carregar corretamente os preços. Entre em contato com o suporte."
              id="c2G61y"
            />
          </p>
        )}

        {monthlyPrice && (
          <button
            disabled={checkoutProduct.loading}
            onClick={() => checkoutProduct.execute(ICheckoutProduct.MonthlyRecurringSubscription)}
            type="button"
            className={classNames(
              "flex w-full items-center justify-center rounded-lg bg-slate-50 py-3 text-sm font-bold text-slate-600",
              "bg-sky-100 text-sky-700",
              "dark:bg-teal-600/20 dark:text-teal-100 dark:hover:bg-teal-600/40",
              "disabled:pointer-events-none disabled:cursor-not-allowed",
            )}
          >
            {checkoutProduct.loading ? (
              <Spinner />
            ) : (
              <FormattedMessage
                defaultMessage="Assinar agora por {formattedAmount}/mês"
                values={{ formattedAmount: monthlyPrice.formattedAmount }}
                id="QMgbdD"
              />
            )}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
